import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Users, ArrowRight, Crown, Clock, Copy, ShieldCheck } from "lucide-react";
import { PlanPicker } from "@/pages/Friends";
import usePageMeta from "@/hooks/usePageMeta";

const Stat = ({ label, value, color }) => (
  <div>
    <div className="text-white/40 text-[10px] uppercase tracking-widest">{label}</div>
    <div className="font-display text-2xl" style={color ? { color } : undefined}>{value}</div>
  </div>
);

/** Public invite landing: a signed-out friend sees the challenge first, then signs in. */
export default function JoinChallenge() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user, loading, login } = useAuth();
  const [preview, setPreview] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  usePageMeta({ title: "Join a Challenge · PickemSports.club", path: `/join/${code}`, noindex: true });

  useEffect(() => {
    if (loading) return;
    const url = user ? `/friends/join/${code}` : `/invite/${code}/preview`;
    api.get(url)
      .then(({ data }) => setPreview(data))
      .catch((e) => setError(e?.response?.data?.detail || "That code doesn't match a challenge."));
    if (user) api.get("/friends/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, [code, user, loading]);

  const signIn = () => {
    localStorage.setItem("pickem_invite_code", String(code).toUpperCase());
    login();
  };

  const join = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/friends/join", { code });
      toast.success(data.already_member ? "You're already in" : "You're in! Make your picks.");
      navigate(`/contest/${data.contest_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not join");
      setBusy(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(`${window.location.origin}/api/invite/${code}`);
    toast.success("Invite link copied — it shows a preview card when you paste it");
  };

  const Frame = ({ children }) =>
    user ? (
      <Layout>{children}</Layout>
    ) : (
      <div className="min-h-screen bg-[#0a0a0a] text-white">
        <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl">
          <div className="max-w-xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 hover:opacity-80" data-testid="join-nav-home">
              <img src="/brand-logo.png" alt="PickemSports.club" className="w-9 h-9 rounded-full" />
              <span className="font-display text-lg sm:text-xl tracking-widest">
                PICKEMSPORTS<span className="text-[#00FF66]">.CLUB</span>
              </span>
            </Link>
            <span className="text-white/40 text-[10px] uppercase tracking-widest">Invite</span>
          </div>
        </header>
        <main className="max-w-xl mx-auto px-4 py-8">{children}</main>
      </div>
    );

  if (error) {
    return (
      <Frame>
        <div className="max-w-md mx-auto text-center py-16" data-testid="join-error">
          <h1 className="font-display text-4xl mb-3">CODE NOT FOUND</h1>
          <p className="text-white/50 text-sm mb-6">{error}</p>
          <Link
            to={user ? "/friends" : "/"}
            className="bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest px-5 py-2.5 text-sm rounded-sm"
          >
            {user ? "Back to Friends Only" : "See how it works"}
          </Link>
        </div>
      </Frame>
    );
  }

  if (loading || !preview) {
    return (
      <Frame>
        <div className="text-white/40 py-20 text-center font-display text-2xl tracking-widest animate-pulse">
          LOADING...
        </div>
      </Frame>
    );
  }

  const full = preview.members >= preview.max_entries;
  const spotsLeft = preview.spots_left ?? Math.max(0, preview.max_entries - preview.members);

  return (
    <Frame>
      <div className="max-w-xl mx-auto py-2" data-testid="join-preview">
        <div className="text-[#00FF66] text-xs uppercase tracking-widest mb-2">You&apos;ve been invited</div>
        <h1 className="font-display text-4xl sm:text-5xl tracking-tight mb-1">{preview.title}</h1>
        <div className="text-white/40 text-xs uppercase tracking-widest mb-6 flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><Crown size={12} /> {preview.manager_name}</span>
          <span>·</span>
          <span>{preview.sport} {preview.position}</span>
          <span>·</span>
          <span className="inline-flex items-center gap-1">
            <Clock size={12} /> locks {new Date(preview.lock_time).toLocaleString()}
          </span>
        </div>

        <div className="border border-white/10 bg-[#141414] p-5 mb-4">
          <div className="grid grid-cols-4 gap-3 text-center">
            <Stat label="Invited" value={`${preview.members}/${preview.max_entries}`} />
            <Stat label="Spots left" value={spotsLeft} color={spotsLeft ? "#00FF66" : "#FF3B30"} />
            <Stat label="Entry" value="FREE" color="#00FF66" />
            <Stat label="Prize" value="PRIDE" color="rgba(255,255,255,0.5)" />
          </div>
          <p className="text-white/50 text-sm mt-4">
            Pick the top 5 {preview.position}s for the slate, set the manager&apos;s tiebreaker, then watch
            the live board together and talk trash in the challenge chat. No entry fee is ever charged
            and no cash prizes are paid.
          </p>
        </div>

        {!user ? (
          <>
            <button onClick={signIn} data-testid="join-signin-btn"
              className="w-full py-3.5 bg-[#00FF66] hover:bg-[#00e65b] text-black font-bold uppercase tracking-widest text-sm rounded-sm inline-flex items-center justify-center gap-2">
              Sign in with Google to join <ArrowRight size={15} />
            </button>
            <div className="mt-3 text-white/40 text-xs flex items-start gap-2">
              <ShieldCheck size={14} className="text-[#00FF66] flex-shrink-0 mt-0.5" />
              <span>
                18+. We&apos;ll bring you straight back to this challenge after you sign in. Playing needs a
                membership from $1.99/mo — the challenge itself is always free.
              </span>
            </div>
            <button onClick={copyLink} data-testid="join-copy-link-btn"
              className="mt-4 text-white/40 hover:text-white text-[10px] uppercase tracking-widest inline-flex items-center gap-1.5">
              <Copy size={11} /> Copy this invite
            </button>
          </>
        ) : preview.plan_covers ? (
          <button
            onClick={join}
            disabled={busy || full}
            data-testid="join-now-btn"
            className="w-full py-3 bg-[#00FF66] hover:bg-[#00e65b] disabled:opacity-50 text-black font-bold uppercase tracking-widest text-sm rounded-sm inline-flex items-center justify-center gap-2"
          >
            <Users size={15} />
            {full ? "Challenge is full" : busy ? "Joining..." : preview.already_member ? "Open challenge" : "Join challenge"}
            {!full && <ArrowRight size={15} />}
          </button>
        ) : (
          <>
            <div className="border border-[#FF9F0A]/40 bg-[#FF9F0A]/10 p-4 mb-4 text-[#FF9F0A] text-sm"
              data-testid="join-needs-plan">
              A membership covering {preview.sport} is needed to play. It&apos;s the only charge —
              the challenge itself is free and pays no cash prizes.
            </div>
            {catalog && <PlanPicker catalog={catalog} />}
          </>
        )}
      </div>
    </Frame>
  );
}
